import type { CardSettlementPayload } from '@contract';
import { formatMoney } from '@sao/shared/money';
import type { DiffRow } from '@/lib/approvals';

/** The most card payments one settlement request names. */
export const CARD_SETTLEMENT_MAX_PAYMENTS = 500;

const SCHEME_NAME: Record<string, string> = {
  VISA: 'Visa',
  MASTERCARD: 'Mastercard',
  MASTER: 'Mastercard',
  AMEX: 'American Express',
  AMERICAN_EXPRESS: 'American Express',
  DISCOVER: 'Discover',
  VERVE: 'Verve',
};

/** "Visa •••• 4242". Brand alone or the last four alone still read; nothing known is null. */
export function cardLabel(
  scheme: string | null | undefined,
  last4: string | null | undefined,
): string | null {
  const brand = scheme?.trim()
    ? (SCHEME_NAME[scheme.trim().toUpperCase().replaceAll(' ', '_')] ??
      scheme.trim())
    : null;
  const digits = last4?.trim() ? `•••• ${last4.trim()}` : null;
  if (brand && digits) return `${brand} ${digits}`;
  return brand ?? digits;
}

export type SettlementOutcome = 'empty' | 'exact' | 'charge' | 'over';

export interface SettlementFigures {
  paymentsMinor: number;
  creditMinor: number;
  /** The credit less the payments: above zero means the credit is the larger. */
  differenceMinor: number;
  /** What would be booked as a card charge out of the pool. */
  chargeMinor: number;
  outcome: SettlementOutcome;
}

/**
 * The three figures of a settlement. A credit smaller than the payments books
 * the gap as a card charge; a larger credit cannot be sent.
 */
export function settlementFigures(
  paymentsMinor: number,
  creditMinor: number,
): SettlementFigures {
  const differenceMinor = creditMinor - paymentsMinor;
  let outcome: SettlementOutcome = 'exact';
  if (paymentsMinor <= 0) outcome = 'empty';
  else if (differenceMinor > 0) outcome = 'over';
  else if (differenceMinor < 0) outcome = 'charge';
  return {
    paymentsMinor,
    creditMinor,
    differenceMinor,
    chargeMinor: Math.max(0, -differenceMinor),
    outcome,
  };
}

export const SETTLEMENT_OVER_WORDS =
  'This credit is more than the payments add up to. It may include a card that was charged but never credited. Leave it for a person to look into.';

/** Ticks or unticks one payment. Ticking past the limit changes nothing. */
export function toggleId(
  ids: readonly string[],
  id: string,
  max = CARD_SETTLEMENT_MAX_PAYMENTS,
): string[] {
  if (ids.includes(id)) return ids.filter((item) => item !== id);
  return ids.length >= max ? [...ids] : [...ids, id];
}

export const REASON_MIN = 8;
export const REASON_MAX = 500;

export interface SettlementDraft {
  lineId: string | null;
  lineClaimed: boolean;
  selectedCount: number;
  figures: SettlementFigures | null;
  reason: string;
}

/** Why the request cannot be sent yet, or null when it can. */
export function settlementBlocker(draft: SettlementDraft): string | null {
  if (!draft.lineId) return 'Pick the pool credit.';
  if (draft.lineClaimed) return 'Another request already waits on this credit.';
  if (draft.selectedCount === 0) return 'Tick the card payments it covers.';
  if (draft.selectedCount > CARD_SETTLEMENT_MAX_PAYMENTS) {
    return `A request covers up to ${CARD_SETTLEMENT_MAX_PAYMENTS} payments.`;
  }
  if (draft.figures?.outcome === 'over') return SETTLEMENT_OVER_WORDS;
  const length = draft.reason.trim().length;
  if (length < REASON_MIN || length > REASON_MAX) {
    return `Give a reason of ${REASON_MIN} to ${REASON_MAX} characters.`;
  }
  return null;
}

export function settlementPayload(
  lineId: string,
  ids: readonly string[],
  reason: string,
): CardSettlementPayload {
  return { line_id: lineId, collection_ids: [...ids], reason: reason.trim() };
}

function count(n: number): string {
  return `${n} card payment${n === 1 ? '' : 's'}`;
}

/**
 * A card_settlement request in words, for Approvals: the credit, the payments
 * and the charge, read from what the server checked (`current`).
 */
export function cardSettlementRows(
  payload: Readonly<Record<string, unknown>>,
  current: Readonly<Record<string, unknown>> | null,
): DiffRow[] {
  const currency =
    typeof current?.currency === 'string' ? current.currency : '';
  const money = (value: unknown) =>
    typeof value === 'number' && currency ? formatMoney(value, currency) : '—';
  const text = (value: unknown) =>
    typeof value === 'string' && value ? value : null;
  const ids = Array.isArray(payload.collection_ids)
    ? payload.collection_ids.length
    : 0;
  const payments =
    typeof current?.payments === 'number' ? current.payments : ids;
  const credit = [
    money(current?.line_amount_minor),
    text(current?.line_reference),
    text(current?.line_value_date),
  ]
    .filter((part) => part && part !== '—')
    .join(' · ');
  const narration = text(current?.line_narration);
  const shortfall =
    typeof current?.shortfall_minor === 'number' ? current.shortfall_minor : 0;
  const rows: DiffRow[] = [
    {
      key: 'line_id',
      label: 'Pool credit',
      before: '—',
      after: narration ? `${credit}\n${narration}` : credit || '—',
      changed: true,
    },
    {
      key: 'collection_ids',
      label: 'Card payments settled',
      before: 'Not settled',
      after: `${count(payments)}, ${money(current?.total_minor)} together`,
      changed: true,
    },
    {
      key: 'shortfall_minor',
      label: 'Booked as a card charge',
      before: '—',
      after: shortfall > 0 ? money(shortfall) : 'Nothing',
      changed: shortfall > 0,
    },
  ];
  const reason = text(payload.reason);
  if (reason) {
    rows.push({
      key: 'reason',
      label: 'Reason',
      before: '—',
      after: reason,
      changed: true,
    });
  }
  return rows;
}
